import BigNumber from 'bignumber.js'
import { ChainIdEnum } from 'config/constants/network'
import tokens from 'config/constants/tokens'
import { DepositCashbackCommonBonus } from 'config/types/bonus'
import { DepositCashbackBonus } from 'config/types/bonus/userBonus'
import { RowBetween } from 'layout/Components/Row'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useTokenUsdPrices } from 'state/app/hooks'
import styled from 'styled-components'
import { Icons } from 'svgs'
import colors from 'theme/ui/colors'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text/Text'
import { buildPluralizeText } from 'utils'
import {
  estimateFiatDecimalValues,
  formatDisplayEstimateFiat,
  isFreeSpinBonusFiatReward,
  isWagerBonusFiatReward,
} from 'utils/bonus'
import BonusDetailStepCard from '../BonusDetailStepCard'

interface CashbackActiveSectionDetailsProps {
  bonus: DepositCashbackBonus | DepositCashbackCommonBonus
  step: number
  isActive: boolean
  isProcessing: boolean
}

const CashbackActiveSectionDetails: React.FC<CashbackActiveSectionDetailsProps & BoxProps> = ({
  bonus,
  step,
  isActive,
  isProcessing,
  ...props
}) => {
  const { t } = useTranslation()
  const tokenUsdPrices = useTokenUsdPrices()

  return (
    <Box {...props}>
      <BonusDetailStepCard
        isActive={isActive}
        isProcessing={isProcessing}
        cardTitle={
          <Flex flexDirection="column">
            <Text
              textTransform="uppercase"
              fontSize="10px"
              fontWeight="600"
              ml="8px"
              lineHeight="normal"
              color="textAlt1"
            >
              <Trans>Step {{ step }}</Trans>
            </Text>
            <Text fontSize="14px" fontWeight="600" mb="4px" ml="8px" lineHeight="22px">
              <Trans>Wagering</Trans>
            </Text>
          </Flex>
        }
      >
        <Box borderRadius="8px" ml="8px" mt="12px">
          <RowBetween mb="16px">
            <Text fontSize="14px" fontWeight="300" color="textSubtle">
              <Trans>Cashback rate</Trans>
            </Text>
            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              {bonus.cashbackRate}%
            </Text>
          </RowBetween>

          <RowBetween mb="16px">
            <Text fontSize="14px" fontWeight="300" color="textSubtle">
              <Trans>Max cashback</Trans>
            </Text>
            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              ${bonus.maxBonusAmountInUsd}
            </Text>
          </RowBetween>

          <RowBetween mb="16px">
            <Flex>
              <Text fontSize="14px" fontWeight="300" color="textSubtle">
                <Trans>Cashback Duration</Trans>
              </Text>
              <Box
                data-tooltip-id="common-tip-bottom"
                data-tooltip-content={t('The time window to calculate your net losses for cashback')}
                ml={2}
                height="16px"
              >
                <Icons.InfoCircleIcon width="16px" fill={colors.textDisable} />
              </Box>
            </Flex>
            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              {t(
                buildPluralizeText('{{duration}} {{hour}}', [
                  {
                    number: bonus.cashbackActiveDurationInHours,
                    key: 'hour',
                    word: ['hour', 'hours'],
                  },
                ]),
                { duration: bonus.cashbackActiveDurationInHours },
              )}
            </Text>
          </RowBetween>

          <RowBetween mb="16px">
            <Text fontSize="14px" fontWeight="300" color="textSubtle">
              <Trans>Min bet for Wager</Trans>
            </Text>
            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              ${bonus.minBet}{' '}
              <StyledFiatEstimated>
                {isFreeSpinBonusFiatReward(bonus)
                  ? formatDisplayEstimateFiat(
                      new BigNumber(bonus.minBet)
                        .dividedBy(tokenUsdPrices[(bonus as any).freeSpinCurrency])
                        .toFixed(
                          estimateFiatDecimalValues(tokens[ChainIdEnum.FIAT][(bonus as any)?.freeSpincurrency]),
                          BigNumber.ROUND_UP,
                        ),
                      (bonus as any).freeSpinCurrency,
                    )
                  : isWagerBonusFiatReward(bonus)
                  ? formatDisplayEstimateFiat(
                      new BigNumber(bonus.minBet)
                        .dividedBy(tokenUsdPrices[bonus.bonusAmount.token.code])
                        .toFixed(
                          estimateFiatDecimalValues(tokens[ChainIdEnum.FIAT][bonus.bonusAmount.token.code]),
                          BigNumber.ROUND_UP,
                        ),
                      bonus.bonusAmount.token.code,
                    )
                  : ''}
              </StyledFiatEstimated>
            </Text>
          </RowBetween>

          <RowBetween mb="16px">
            <Text fontSize="14px" fontWeight="300" color="textSubtle">
              <Trans>Max bet for Wager</Trans>
            </Text>
            <Text fontSize="14px" fontWeight="600" color="textTertiary">
              ${bonus.maxBet}{' '}
              <StyledFiatEstimated>
                {isFreeSpinBonusFiatReward(bonus)
                  ? formatDisplayEstimateFiat(
                      new BigNumber(bonus.maxBet)
                        .dividedBy(tokenUsdPrices[(bonus as any).freeSpinCurrency])
                        .toFixed(
                          estimateFiatDecimalValues(tokens[ChainIdEnum.FIAT][(bonus as any)?.freeSpincurrency]),
                          BigNumber.ROUND_DOWN,
                        )
                        .toString(),
                      (bonus as any).freeSpinCurrency,
                    )
                  : isWagerBonusFiatReward(bonus)
                  ? formatDisplayEstimateFiat(
                      new BigNumber(bonus.maxBet)
                        .dividedBy(tokenUsdPrices[bonus.bonusAmount.token.code])
                        .toFixed(
                          estimateFiatDecimalValues(tokens[ChainIdEnum.FIAT][bonus.bonusAmount.token.code]),
                          BigNumber.ROUND_DOWN,
                        )
                        .toString(),
                      bonus.bonusAmount.token.code,
                    )
                  : ''}
              </StyledFiatEstimated>
            </Text>
          </RowBetween>
        </Box>
      </BonusDetailStepCard>
    </Box>
  )
}

const StyledFiatEstimated = styled(Text).attrs({ as: 'span' })`
  color: ${({ theme: { colors } }) => colors.textDisable};
  font-size: inherit;
  font-weight: inherit;
`

export default CashbackActiveSectionDetails
